import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import deepEqual from 'deep-equal';

import {
  AddressForm,
  apiCallFinished,
  envConfigSelectors,
  errorMessages,
  errorMessagesActions,
  errorTypes,
  infoTypes,
  Messages,
  messagesActions,
  PostcodeSearchFormDescription as Description,
  preloadSteps,
  ScreenNav,
  scrollToMessageTypeAnchor,
  SearchFormContainer,
  withProgressBar,
  withRestorableDraft,
} from '~/components/index';

import {
  addressesApi,
  api,
  appointmentDraftActions,
  appointmentDraftHelpers,
  appointmentDraftSelectors,
  journeyStateActions,
  locationApi,
  shopsActions,
} from '~/api';
import { formatPostcode, getRandomNumber, isNotEmpty, scrollToAnchor } from '~/utils/index';
import { validation } from '~/components/questions/helpers';

const { validateGBPostcode, POSTCODE_INVALID_ERROR_MESSAGE } = validation;

const defaultAddress = 'default';
const PostcodeSearchFormDescriptionScrollAnchor = scrollToMessageTypeAnchor(Description);

const {
  NO_ADDRESSES_FOUND_ERROR,
  ADDRESS_NOT_ENTERED_ERROR,
  ADDRESS_NOT_SELECTED_ERROR,
  NO_POSTCODE_FOUND_ERROR,
  POSTCODE_OUT_OF_AREA_ERROR,
} = errorTypes;
const { ADDRESS_NOT_FOUND } = infoTypes;
const { formatAddressQuestionToDraftAddress, formatDraftAddressToAddressQuestion } =
  appointmentDraftHelpers;

import styles from './PostcodeSearchForm.scss';

export class PostcodeSearchForm extends PureComponent {
  state = {
    context: {
      searchFormTitle: 'Find a shop',
      isSearchTermInvalid: false,
      searchFormInlineErrorMsg: POSTCODE_INVALID_ERROR_MESSAGE,
    },
    addresses: null,
    isAddressFormVisible: false,
    restoreFromForm: false,
    selectedAddress: defaultAddress,
    isAjaxCompleted: false,
    isCurrentLocationLoading: false,
  };

  componentDidMount() {
    const { dispatchApiCallFinished, dispatchPushMessage, storeSearchQuery, address } = this.props;
    const selectedAddress = address ? JSON.stringify(address) : defaultAddress;

    if (storeSearchQuery) {
      dispatchApiCallFinished(false);
      addressesApi
        .fetchAddresses(storeSearchQuery)
        .then((addresses) => {
          const foundAddress = addresses.find((a) => deepEqual(a, address));

          const isManualAddress =
            !!address && !foundAddress && Object.values(address).some((value) => isNotEmpty(value));

          if (!addresses.length && !address) {
            dispatchPushMessage({
              id: getRandomNumber(0, 1000),
              type: ADDRESS_NOT_FOUND,
            });
          }

          this.setState({
            addresses,
            isAjaxCompleted: true,
            isAddressFormVisible: isManualAddress || !addresses.length,
            restoreFromForm: isManualAddress,
            selectedAddress: foundAddress ? JSON.stringify(foundAddress) : defaultAddress,
          });
        })
        .then(() => dispatchApiCallFinished(true));
    } else {
      dispatchApiCallFinished(true);
      // Show address form if there's an address with details (manual entry)
      const hasManualAddress = address && Object.values(address).some((val) => val && val !== '');
      this.setState({
        isAjaxCompleted: true,
        isAddressFormVisible: hasManualAddress,
        selectedAddress,
      });
    }
    window.scrollTo(0, 0);
  }

  findHandler = (searchQuery) => {
    const { dispatchApiCallFinished, dispatchSetSearchQuery } = this.props;
    const errorMessage = validateGBPostcode(searchQuery);

    if (errorMessage) {
      return this.invalidPostcodeHandler(errorMessage, searchQuery);
    }
    dispatchApiCallFinished(false);
    dispatchSetSearchQuery(searchQuery);

    return this.executeIfServiceAvailable(searchQuery, () => this.findAddresses(searchQuery));
  };

  executeIfServiceAvailable = (postcode, execute) => {
    const { checkPostcodeAvailability, appointmentTypeId } = this.props;
    return checkPostcodeAvailability
      ? checkPostcodeAvailability(postcode, appointmentTypeId).then(
          () => execute(),
          ({ status }) => {
            if (status === 422) {
              this.showOutOfAreaError();
            }
          },
        )
      : execute();
  };

  showOutOfAreaError = () => {
    const {
      dispatchApiCallFinished,
      dispatchResetErrorMessagesWith,
      errorMessageForUnsupportedPostcode,
    } = this.props;

    dispatchResetErrorMessagesWith([errorMessageForUnsupportedPostcode]);
    this.setState(
      {
        addresses: null,
      },
      () => dispatchApiCallFinished(true),
    );
  };

  findAddresses = (searchQuery) => {
    const { dispatchClearErrorMessages, dispatchPushMessage, dispatchApiCallFinished } = this.props;

    return addressesApi.fetchAddresses(searchQuery).then((addresses) => {
      dispatchClearErrorMessages();
      if (!addresses.length) {
        dispatchPushMessage({
          id: getRandomNumber(0, 1000),
          type: ADDRESS_NOT_FOUND,
        });
        this.setState(
          {
            restoreFromForm: false,
            isAddressFormVisible: true,
            addresses: null,
          },
          () => dispatchApiCallFinished(true),
        );
      } else {
        this.setState(
          (prevState) => ({
            restoreFromForm: false,
            selectedAddress: defaultAddress,
            isAddressFormVisible: false,
            context: {
              ...prevState.context,
              isSearchTermInvalid: false,
            },
            addresses,
          }),
          () => dispatchApiCallFinished(true),
        );
      }
    });
  };

  useCurrentLocationHandler = (coordinates) => {
    this.setState({
      isCurrentLocationLoading: true,
    });

    locationApi.getPostcode(coordinates).then(({ postcode }) => {
      this.setState({ isCurrentLocationLoading: false });
      this.findHandler(postcode);
    });
  };

  invalidPostcodeHandler = (errorMessage, searchQuery) => {
    const { dispatchSetSearchQuery } = this.props;
    dispatchSetSearchQuery(searchQuery);
    this.setState((prevState) => ({
      context: {
        ...prevState.context,
        isSearchTermInvalid: true,
        searchFormInlineErrorMsg: errorMessage,
      },
      addresses: null,
    }));
  };

  shopsNotFoundErrorHandler = (searchQuery) => {
    const { dispatchResetErrorMessagesWith } = this.props;
    const actionMessage = {
      type: NO_POSTCODE_FOUND_ERROR,
      description: errorMessages[NO_POSTCODE_FOUND_ERROR].createDescription(searchQuery),
    };
    dispatchResetErrorMessagesWith([actionMessage]);
    // Form validation is now handled by AddressForm's react-final-form validation
    return Promise.reject();
  };

  toggleAddressFormState = () => {
    const { isAddressFormVisible, context } = this.state;
    const { dispatchClearMessages, dispatchClearErrorMessages, dispatchSetSearchQuery } =
      this.props;
    dispatchClearMessages();
    dispatchClearErrorMessages();
    if (isAddressFormVisible) {
      scrollToAnchor('address-title');
      dispatchSetSearchQuery('');
      this.setState({
        addresses: null,
        isAddressFormVisible: false,
        context: {
          ...context,
          isSearchTermInvalid: false,
        },
      });
    } else {
      this.setState({
        isAddressFormVisible: true,
      });
    }
  };

  constructInitialValues = () => {
    const { address, storeSearchQuery } = this.props;
    const { restoreFromForm } = this.state;
    if (restoreFromForm) {
      return { address: formatDraftAddressToAddressQuestion(address) };
    }
    return { address: { postcode: formatPostcode(storeSearchQuery) } };
  };

  selectAddressHandler = (event) => {
    const { value: selectedAddress } = event.target;
    const { dispatchClearErrorMessages } = this.props;
    dispatchClearErrorMessages();
    this.setState({
      selectedAddress,
    });
  };

  proceedToNextPage = () => {
    const {
      history,
      nextPath,
      dispatchClearErrorMessages,
      dispatchClearMessages,
      dispatchSaveDraft,
      dispatchSaveJourneyState,
    } = this.props;

    dispatchClearMessages();
    dispatchClearErrorMessages();
    dispatchSaveDraft()
      .then(() => dispatchSaveJourneyState())
      .then(() => history.pushWithToken(nextPath));
  };

  onNextClick = () => {
    const { selectedAddress } = this.state;
    const {
      dispatchResetErrorMessagesWith,
      dispatchSelectAddressDescription,
      dispatchSetEmptyAddress,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchLoadShops,
      dispatchSelectAddress,

      storeSearchQuery,
      isMeasureService,
    } = this.props;

    if (selectedAddress === defaultAddress) {
      dispatchResetErrorMessagesWith([{ type: ADDRESS_NOT_SELECTED_ERROR }]);
    } else {
      const addressDescription = JSON.parse(selectedAddress);

      dispatchApiCallFinished(false);

      let promiseResult;

      if (isMeasureService) {
        promiseResult = Promise.all([
          dispatchSelectAddressDescription(addressDescription),
          addressesApi
            .fetchAddress(addressDescription.id)
            .then((address) => dispatchSelectAddress(address)),
        ]);
      } else {
        promiseResult = Promise.all([
          dispatchSelectAddressDescription(addressDescription),
          dispatchSetEmptyAddress(),
          dispatchLoadShops(storeSearchQuery).then(({ shops }) => this.selectShop(shops[0])),
        ]);
      }

      promiseResult
        .then(() => dispatchUpdateAppointmentType())
        .then(() => this.proceedToNextPage());
    }
  };

  onNextAddressFormClick = ({ address }) => {
    const { dispatchApiCallFinished } = this.props;
    dispatchApiCallFinished(false);
    this.executeIfServiceAvailable(address.postcode, () => {
      this.addressFormSubmit(address);
    });
  };

  addressFormSubmit = (address) => {
    const {
      dispatchLoadShops,
      dispatchSelectAddress,
      dispatchSetEmptyAddressDescription,
      dispatchApiCallFinished,
      dispatchUpdateAppointmentType,
      isMeasureService,
    } = this.props;
    const loadShopPromise = isMeasureService
      ? Promise.resolve()
      : dispatchLoadShops(address.postcode).then(({ shops }) =>
          shops.length > 0
            ? this.selectShop(shops[0])
            : this.shopsNotFoundErrorHandler(address.postcode),
        );
    loadShopPromise
      .then(() => dispatchUpdateAppointmentType())
      .then(() => dispatchSelectAddress(formatAddressQuestionToDraftAddress({ ...address })))
      .then(() => dispatchSetEmptyAddressDescription())
      .then(this.proceedToNextPage)
      .catch(() => dispatchApiCallFinished(true));
  };

  selectShop = (shop) => {
    const { selectedShop, dispatchSelectShop } = this.props;

    if (selectedShop.id !== shop.id) {
      dispatchSelectShop(shop);
    }
  };

  render() {
    const {
      dispatchApiCallFinished,
      dispatchResetErrorMessagesWith,
      dispatchRemoveSingleErrorMessage,
      storeSearchQuery,
      postcodeSearchFormAdditionalInfo,
      postcodeSearchFormSubtitle,
      postcodeSearchFormTitle,
      landingPageUrl,
      addressFormLinkLabel,
      buildDefaultOptionLabel,
      errorMessageForUnsupportedPostcode,
    } = this.props;
    const {
      context,
      isAjaxCompleted,
      addresses,
      isAddressFormVisible,
      selectedAddress,
      isCurrentLocationLoading,
    } = this.state;
    const postcodeFormContent = isAddressFormVisible ? (
      <AddressForm
        hideAddressFormHandler={this.toggleAddressFormState}
        onSubmit={this.onNextAddressFormClick}
        initialValues={this.constructInitialValues()}
        clearMessages={messagesActions.clearMessages}
        resetErrorMessages={errorMessagesActions.resetErrorMessagesWith}
        removeSingleErrorMessage={dispatchRemoveSingleErrorMessage}
      >
        <Messages
          forTypes={[NO_ADDRESSES_FOUND_ERROR, ADDRESS_NOT_ENTERED_ERROR, NO_POSTCODE_FOUND_ERROR]}
        />
        <ScreenNav externalPrevPath={landingPageUrl} submit />
      </AddressForm>
    ) : (
      <div>
        <SearchFormContainer
          className={styles.PostcodeSearchForm}
          context={context}
          addresses={addresses}
          selectedAddress={selectedAddress}
          apiCallFinished={dispatchApiCallFinished}
          dispatchResetErrorMessagesWith={dispatchResetErrorMessagesWith}
          selectAddressHandler={this.selectAddressHandler}
          toggleAddressFormState={this.toggleAddressFormState}
          findHandler={this.findHandler}
          useCurrentLocationHandler={this.useCurrentLocationHandler}
          isCurrentLocationLoading={isCurrentLocationLoading}
          storeSearchQuery={storeSearchQuery}
          validate={validateGBPostcode}
          addressFormLinkLabel={addressFormLinkLabel}
          buildDefaultOptionLabel={buildDefaultOptionLabel}
        >
          <Messages forTypes={[NO_ADDRESSES_FOUND_ERROR]} />
          <Messages forTypes={[ADDRESS_NOT_SELECTED_ERROR]} />
        </SearchFormContainer>
        <ScreenNav
          onNextClick={this.onNextClick}
          externalPrevPath={landingPageUrl}
          hideNextButton={!addresses}
        />
      </div>
    );

    return (
      <section data-testid="Postcode" data-test-ajax={isAjaxCompleted}>
        <PostcodeSearchFormDescriptionScrollAnchor
          title={postcodeSearchFormTitle}
          subtitle={postcodeSearchFormSubtitle}
          additionalInfo={postcodeSearchFormAdditionalInfo}
          forTypes={[
            NO_ADDRESSES_FOUND_ERROR,
            ADDRESS_NOT_ENTERED_ERROR,
            ADDRESS_NOT_FOUND,
            ADDRESS_NOT_SELECTED_ERROR,
            NO_POSTCODE_FOUND_ERROR,
            errorMessageForUnsupportedPostcode,
          ]}
        />
        <div className={styles.ErrorPostCodOutOfArea}>
          <Messages forTypes={[ADDRESS_NOT_FOUND, POSTCODE_OUT_OF_AREA_ERROR]} />
        </div>
        {postcodeFormContent}
      </section>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    appointment: {
      draft: {
        shop,
        appointmentType: { id: appointmentTypeId },
      },
    },
    journeyState: { searchQuery: storeSearchQuery },
    services: {
      config: { shopsNextPath: nextPath },
    },
  } = state;

  const postcodeSearchFormConfig =
    envConfigSelectors.selectPostcodeSearchFormConfig(appointmentTypeId)(state);

  return {
    appointmentTypeId,
    selectedShop: shop,
    storeSearchQuery,
    address: appointmentDraftSelectors.selectAddress(state),
    landingPageUrl: envConfigSelectors.selectLandingPage(
      api.SERVICE_NAME,
      appointmentTypeId,
    )(state),
    nextPath,
    postcodeSearchFormTitle: postcodeSearchFormConfig.title,
    postcodeSearchFormSubtitle: postcodeSearchFormConfig.subtitle,
    postcodeSearchFormAdditionalInfo: postcodeSearchFormConfig.additionalFormDescription,
    checkPostcodeAvailability: postcodeSearchFormConfig.checkPostcodeAvailability,
    isMeasureService: envConfigSelectors.selectIsMeasureService()(state),
    addressFormLinkLabel:
      envConfigSelectors.selectAddressDropdownAddressFormLinkLabel(appointmentTypeId)(state),
    buildDefaultOptionLabel:
      envConfigSelectors.selectAddressDropdownBuildDefaultOptionLabel(appointmentTypeId)(state),
    errorMessageForUnsupportedPostcode:
      envConfigSelectors.selectErrorMessageForUnsupportedPostcode(appointmentTypeId)(state),
  };
};

const mapDispatchToProps = {
  dispatchSaveDraft: appointmentDraftActions.saveAppointmentDraft,
  dispatchSetSearchQuery: journeyStateActions.setSearchQuery,
  dispatchSaveJourneyState: journeyStateActions.saveJourneyState,
  dispatchUpdateAppointmentType: appointmentDraftActions.updateAppointmentType,
  dispatchLoadShops: shopsActions.loadShops,
  dispatchSelectShop: appointmentDraftActions.setShop,
  dispatchSelectAddress: appointmentDraftActions.setAddress,
  dispatchSetEmptyAddress: appointmentDraftActions.setEmptyAddress,
  dispatchSelectAddressDescription: appointmentDraftActions.setAddressDescription,
  dispatchSetEmptyAddressDescription: appointmentDraftActions.setEmptyAddressDescription,
  dispatchApiCallFinished: apiCallFinished,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
  dispatchPushMessage: messagesActions.pushMessage,
  dispatchClearMessages: messagesActions.clearMessages,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
};

export const ConnectedPostcodeSearchForm = preloadSteps(
  withRestorableDraft(
    connect(mapStateToProps, mapDispatchToProps)(withProgressBar(PostcodeSearchForm)),
  ),
);

PostcodeSearchForm.propTypes = {
  history: PropTypes.shape({}).isRequired,
  address: PropTypes.shape({}),
  nextPath: PropTypes.string.isRequired,
  storeSearchQuery: PropTypes.string,
  landingPageUrl: PropTypes.string.isRequired,
  selectedShop: PropTypes.shape({}).isRequired,
  checkPostcodeAvailability: PropTypes.func,
  isMeasureService: PropTypes.bool.isRequired,
  postcodeSearchFormTitle: PropTypes.string,
  postcodeSearchFormSubtitle: PropTypes.string,
  addressFormLinkLabel: PropTypes.string,
  buildDefaultOptionLabel: PropTypes.func,
  appointmentTypeId: PropTypes.string.isRequired,
  errorMessageForUnsupportedPostcode: PropTypes.shape({}),

  dispatchSaveDraft: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchSetSearchQuery: PropTypes.func.isRequired,
  dispatchSaveJourneyState: PropTypes.func.isRequired,
  dispatchLoadShops: PropTypes.func.isRequired,
  dispatchSelectShop: PropTypes.func.isRequired,
  dispatchSelectAddress: PropTypes.func.isRequired,
  dispatchSetEmptyAddress: PropTypes.func.isRequired,
  dispatchSelectAddressDescription: PropTypes.func.isRequired,
  dispatchSetEmptyAddressDescription: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchUpdateAppointmentType: PropTypes.func.isRequired,
  dispatchClearErrorMessages: PropTypes.func.isRequired,
  dispatchPushMessage: PropTypes.func.isRequired,
  dispatchClearMessages: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  postcodeSearchFormAdditionalInfo: PropTypes.node,
};

PostcodeSearchForm.defaultProps = {
  address: null,
  storeSearchQuery: '',
  postcodeSearchFormAdditionalInfo: undefined,
  checkPostcodeAvailability: undefined,
  postcodeSearchFormTitle: undefined,
  postcodeSearchFormSubtitle: undefined,
  addressFormLinkLabel: null,
  buildDefaultOptionLabel: null,
  errorMessageForUnsupportedPostcode: undefined,
};
